import * as React from 'react';
import { Avatar, Empty, Input, Spin, Table, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import { useCampaignAvatarMap, useCampaignPerformance } from '../queryHooks';
import { formatCompact, formatPct, formatVIT } from '../format';

const PerformanceSection: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const campaignId = id ? Number(id) : undefined;
    const { data, isLoading } = useCampaignPerformance(campaignId);
    const avatarById = useCampaignAvatarMap(campaignId);
    const [search, setSearch] = React.useState('');

    if (isLoading) return <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>;
    const rows = (data?.creators ?? []).filter((r) =>
        !search.trim() || r.name.toLowerCase().includes(search.trim().toLowerCase()),
    );

    if (!data) return <Empty />;

    return (
        <div>
            <Input.Search
                allowClear
                placeholder="Search for creator"
                style={{ width: 280, marginBottom: 16 }}
                onChange={(e) => setSearch(e.target.value)}
            />
            <Table
                rowKey="creator_id"
                dataSource={rows}
                pagination={false}
                size="middle"
                scroll={{ x: 'max-content' }}
                columns={[
                    {
                        title: 'Name',
                        dataIndex: 'name',
                        render: (name: string, row) => (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <Avatar src={avatarById[row.creator_id] ? `data:image/jpeg;base64,${avatarById[row.creator_id]}` : undefined} size={28}>
                                    {name?.[0]}
                                </Avatar>
                                {name}
                            </span>
                        ),
                    },
                    { title: 'VIT', dataIndex: 'vit', render: (v: number) => formatVIT(v) },
                    { title: 'Posts', dataIndex: 'posts' },
                    { title: 'Engagements', dataIndex: 'engagements', render: (v: number) => formatCompact(v) },
                    { title: 'Engagement rate', dataIndex: 'engagement_rate', render: (v: number) => formatPct(v) },
                    { title: 'Video views', dataIndex: 'video_views', render: (v: number) => formatCompact(v) },
                    { title: 'Est. impressions', dataIndex: 'est_impressions', render: (v: number) => formatCompact(v) },
                    { title: 'Potential reach', dataIndex: 'potential_reach', render: (v: number) => formatCompact(v) },
                    { title: 'Organic engagements', render: () => <span style={{ color: '#bbb' }}>--</span> },
                    { title: 'Paid engagements', render: () => <span style={{ color: '#bbb' }}>--</span> },
                ]}
                summary={() => (
                    <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>
                            <Typography.Text strong>{data.creators.length} creators</Typography.Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>{formatVIT(data.totals.vit)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>{data.totals.posts}</Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>{formatCompact(data.totals.engagements)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>{formatPct(data.totals.engagement_rate)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={5}>{formatCompact(data.totals.video_views)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={6}>{formatCompact(data.totals.est_impressions)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={7}>{formatCompact(data.totals.potential_reach)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={8}>--</Table.Summary.Cell>
                        <Table.Summary.Cell index={9}>--</Table.Summary.Cell>
                    </Table.Summary.Row>
                )}
            />
        </div>
    );
};

export default PerformanceSection;
